import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheckCircle, FiUploadCloud } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CATEGORIES } from "../components/foundation/foundationUtils";
import { apiRequest, errorMessage, fetchFoundationSettings } from "../api";

const inputClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";
const MAX_IMAGE = 5 * 1024 * 1024;
const MAX_VIDEO = 50 * 1024 * 1024;
const MAX_EXTRA = 8;

const emptyForm = {
  full_name: "", phone: "", email: "", age: "", city: "", country: "", title: "", category: "medical",
  story: "", amount_needed: "", video_url: "",
};

function Section({ title, children }) {
  return (
    <div className="rounded-xl bg-navy-dark border border-navy-border p-6">
      <h2 className="text-lg font-bold mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm text-gray-300 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

// Registration of a need: applicant details, the request, photos and an optional video. Nothing is
// public until an admin reviews and approves it.
export default function FoundationRegisterPage() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [cover, setCover] = useState(null);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Register Your Need - Foundation";
    fetchFoundationSettings().then(setSettings).catch(() => {});
  }, []);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const currency = settings?.currency_label || "PKR";
  const previews = useMemo(() => images.map((f) => URL.createObjectURL(f)), [images]);
  const coverPreview = useMemo(() => (cover ? URL.createObjectURL(cover) : null), [cover]);

  const pickCover = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_IMAGE) return setError("Each image must be 5 MB or smaller.");
    setError("");
    setCover(file || null);
  };

  const pickImages = (e) => {
    const files = [...e.target.files];
    if (files.some((f) => f.size > MAX_IMAGE)) return setError("Each image must be 5 MB or smaller.");
    if (files.length > MAX_EXTRA) return setError(`You can attach at most ${MAX_EXTRA} extra photos.`);
    setError("");
    setImages(files);
  };

  const pickVideo = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_VIDEO) {
      e.target.value = "";
      return setError("The video must be 50 MB or smaller. You can paste a YouTube link instead.");
    }
    setError("");
    setVideo(file || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cover && images.length === 0) return setError("Please attach at least one photo.");
    setBusy(true);
    setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => { if (value !== "") fd.append(key, value); });
    if (cover) fd.append("cover_image", cover);
    images.forEach((f) => fd.append("images", f));
    if (video) fd.append("video", video);
    const { ok, data } = await apiRequest("/core/foundation/cases/", { method: "POST", body: fd });
    setBusy(false);
    if (!ok) return setError(errorMessage(data));
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Header />
      <div className="h-16" />
      <main className="bg-navy text-white min-h-[70vh] py-10">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/foundation" className="inline-block text-accent-light text-sm mb-6 hover:underline">&larr; Back to Foundation</Link>

          {done ? (
            <div className="text-center py-16 rounded-xl bg-navy-dark border border-accent-light/25">
              <FiCheckCircle className="mx-auto text-emerald-400" size={48} />
              <h1 className="text-3xl font-bold mt-4">Request received</h1>
              <p className="text-gray-300 mt-3 max-w-md mx-auto">
                Thank you. Our team will review your request and contact you on the phone number you gave. Once it is approved, it will appear on the Foundation page so people can help.
              </p>
              <Link to="/foundation" className="inline-block mt-8 px-6 py-2.5 rounded-full bg-brand-gradient font-semibold">Back to Foundation</Link>
            </div>
          ) : (
            <>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Register Your Need</h1>
              <p className="text-gray-300 mt-3 mb-8 whitespace-pre-line">{settings?.register_intro}</p>

              <form onSubmit={submit} className="space-y-6">
                <Section title="About you">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Full name *"><input required className={inputClass} value={form.full_name} onChange={set("full_name")} /></Field>
                    <Field label="Phone *" hint="Only our team sees this - it is never published."><input required className={inputClass} value={form.phone} onChange={set("phone")} /></Field>
                    <Field label="Email"><input type="email" className={inputClass} value={form.email} onChange={set("email")} /></Field>
                    <Field label="Age"><input type="number" min="1" max="120" className={inputClass} value={form.age} onChange={set("age")} /></Field>
                    <Field label="City"><input className={inputClass} value={form.city} onChange={set("city")} /></Field>
                    <Field label="Country"><input className={inputClass} value={form.country} onChange={set("country")} /></Field>
                  </div>
                </Section>

                <Section title="Your request">
                  <Field label="Title *"><input required maxLength={150} className={inputClass} placeholder="e.g. Help for my father's heart surgery" value={form.title} onChange={set("title")} /></Field>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Type of help *">
                      <select className={`${inputClass} bg-navy-dark`} value={form.category} onChange={set("category")}>
                        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </Field>
                    <Field label={`Amount needed (${currency})`} hint="Leave empty if you need goods or services, not money.">
                      <input type="number" min="0" step="0.01" className={inputClass} value={form.amount_needed} onChange={set("amount_needed")} />
                    </Field>
                  </div>
                  <Field label="Your story *" hint="Explain your situation and exactly what help you need (at least 30 characters).">
                    <textarea required minLength={30} rows={7} className={inputClass} value={form.story} onChange={set("story")} />
                  </Field>
                </Section>

                <Section title="Photos & video">
                  <Field label="Main photo" hint="Shown on your card. Max 5 MB.">
                    <label className="flex items-center gap-3 cursor-pointer px-4 py-3 rounded-lg border border-dashed border-navy-border hover:border-accent-light text-sm text-gray-300">
                      <FiUploadCloud size={20} /> {cover ? cover.name : "Choose a photo"}
                      <input type="file" accept="image/*" className="hidden" onChange={pickCover} />
                    </label>
                    {coverPreview && <img src={coverPreview} alt="Main preview" className="mt-3 w-40 h-32 object-cover rounded-lg border border-navy-border" />}
                  </Field>
                  <Field label="More photos" hint={`Up to ${MAX_EXTRA} photos, 5 MB each - documents, medical reports, your home, etc.`}>
                    <input type="file" accept="image/*" multiple onChange={pickImages} className="text-sm text-gray-300" />
                    {previews.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {previews.map((src) => <img key={src} src={src} alt="Preview" className="w-20 h-20 object-cover rounded-md border border-navy-border" />)}
                      </div>
                    )}
                  </Field>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Video (optional)" hint="MP4, WebM or MOV, up to 50 MB.">
                      <input type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" onChange={pickVideo} className="text-sm text-gray-300" />
                    </Field>
                    <Field label="…or a YouTube / Vimeo link">
                      <input type="url" className={inputClass} placeholder="https://youtube.com/watch?v=..." value={form.video_url} onChange={set("video_url")} />
                    </Field>
                  </div>
                </Section>

                {error && <p className="text-sm text-red-400">{error}</p>}
                <button disabled={busy} className="px-8 py-3 rounded-full bg-brand-gradient font-semibold shadow-lg shadow-accent/20 disabled:opacity-60">
                  {busy ? "Submitting..." : "Submit Request"}
                </button>
                <p className="text-xs text-gray-500">By submitting you confirm the information is true. Requests are verified before they are published.</p>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
